import { getStoredToken } from '../hooks/useAuth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';

// Callback set by App.tsx so API layer can trigger logout on 401
let onUnauthorized: (() => void) | null = null;
export function setOnUnauthorized(cb: () => void) {
  onUnauthorized = cb;
}

function authHeaders(): Record<string, string> {
  const token = getStoredToken();
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

export async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  const response = await fetch(url, {
    ...init,
    headers: { ...authHeaders(), ...init?.headers },
  });

  if (response.status === 401) {
    // Attempt token refresh before giving up
    const refreshToken = sessionStorage.getItem('artstash_refresh_token');
    if (refreshToken) {
      try {
        const refreshResponse = await fetch(
          `${API_URL}/api/auth/keycloak/refresh`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
          }
        );
        if (refreshResponse.ok) {
          const data = await refreshResponse.json();
          const newToken = data.access_token || data.token;
          if (newToken) {
            sessionStorage.setItem('artstash_token', newToken);
            if (data.refresh_token) {
              sessionStorage.setItem('artstash_refresh_token', data.refresh_token);
            }
            if (data.expires_in) {
              const expiry = Date.now() + data.expires_in * 1000;
              sessionStorage.setItem('artstash_token_expiry', String(expiry));
            }
            // Retry the original request with the new token
            const retryResponse = await fetch(url, {
              ...init,
              headers: {
                ...init?.headers,
                Authorization: `Bearer ${newToken}`,
              },
            });
            return retryResponse;
          }
        }
      } catch {
        // Refresh failed — fall through to logout
      }
    }

    if (onUnauthorized) {
      onUnauthorized();
      throw new Error('Session expired — please log in again');
    }
  }

  return response;
}

export const uploadDocument = async (
  file: File,
  accountId?: string | null,
  accountName?: string | null
) => {
  const formData = new FormData();
  formData.append('file', file);

  const params = new URLSearchParams();
  if (accountId) params.append('account_id', accountId);
  if (accountName) params.append('account_name', accountName);

  const url = `${API_URL}/api/upload${params.toString() ? '?' + params.toString() : ''}`;

  const response = await authFetch(url, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Upload failed');
  }

  return response.json();
};

export const getPlans = async () => {
  const response = await authFetch(`${API_URL}/api/plans`);
  if (!response.ok) {
    throw new Error('Failed to fetch plans');
  }
  return response.json();
};

export const getAccounts = async () => {
  const response = await authFetch(`${API_URL}/api/accounts/`);
  if (!response.ok) {
    throw new Error('Failed to fetch accounts');
  }
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

export const getWorkspaces = async (accountId: string) => {
  const response = await authFetch(`${API_URL}/api/workspaces?accountId=${encodeURIComponent(accountId)}`);
  if (!response.ok) {
    throw new Error('Failed to fetch workspaces');
  }
  const data = await response.json();
  // DAM may return {items: [...]} or a flat array
  if (data && Array.isArray(data.items)) return data.items;
  return Array.isArray(data) ? data : [];
};

export const getAgents = async (accountId: string, workspaceId: string, workspaceName?: string) => {
  const params = new URLSearchParams({ accountId, workspaceId });
  if (workspaceName) params.append('workspaceName', workspaceName);
  const response = await authFetch(`${API_URL}/api/agents?${params.toString()}`);
  if (!response.ok) {
    throw new Error('Failed to fetch agents');
  }
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

export const getSessions = async (accountId: string) => {
  const response = await authFetch(`${API_URL}/api/sessions?accountId=${encodeURIComponent(accountId)}`);
  if (!response.ok) {
    throw new Error('Failed to fetch sessions');
  }
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

export const getAgentProfile = async (slug: string, accountId: string, workspaceId: string, workspaceName?: string) => {
  const params = new URLSearchParams({ accountId, workspaceId });
  if (workspaceName) params.append('workspaceName', workspaceName);
  const response = await authFetch(`${API_URL}/api/agents/${encodeURIComponent(slug)}/profile?${params.toString()}`);
  if (!response.ok) {
    return null;
  }
  return response.json();
};

export const getAgentAvatarUrl = async (slug: string, accountId: string, workspaceId: string, workspaceName?: string): Promise<string | null> => {
  const params = new URLSearchParams({ accountId, workspaceId });
  if (workspaceName) params.append('workspaceName', workspaceName);
  const response = await authFetch(`${API_URL}/api/agents/${encodeURIComponent(slug)}/profile/avatar?${params.toString()}`);
  if (!response.ok) {
    return null;
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

export const getAgentKnowledgeStatus = async (slug: string, accountId: string, workspaceId: string, workspaceName?: string) => {
  const params = new URLSearchParams({ accountId, workspaceId });
  if (workspaceName) params.append('workspaceName', workspaceName);
  const response = await authFetch(`${API_URL}/api/agents/${encodeURIComponent(slug)}/knowledge-status?${params.toString()}`);
  if (!response.ok) {
    throw new Error('Failed to fetch knowledge status');
  }
  return response.json();
};

export interface SyncProgress {
  stage?: string;
  processed?: number;
  total?: number;
}

export interface SyncAgentOptions {
  /** Called on every poll that reports an in-flight sync. */
  onProgress?: (progress: SyncProgress) => void;
  /** Ceiling for the blocking poll. Exceeding it resolves to `timeout`. */
  timeoutMs?: number;
  pollIntervalMs?: number;
}

// Knowledge graph ingestion is embedding-bound at roughly 5 entities/sec, so a
// 6,500-entity agent takes ~21 minutes. The previous 60s ceiling (roadmap #170)
// made every large sync resolve as `timeout` while the backend was still
// happily working, which the sidebar then rendered as empty counts.
export const SYNC_POLL_INTERVAL_MS = 2000;
export const SYNC_POLL_TIMEOUT_MS = 30 * 60 * 1000;

// The sync is scheduled as a FastAPI background task, so it starts only after
// the kickoff response has been sent. `no_sync` is therefore expected for the
// first poll or two and must not be treated as "nothing happened" — that race
// is another route to the misleading revert this fix exists to remove.
export const SYNC_NO_SYNC_GRACE_MS = 30 * 1000;

/**
 * True when a sync-status payload represents a finished sync.
 *
 * Callers must not refresh knowledge status on a non-terminal result: knowledge
 * graph ingestion commits only at the very end, so reading counts mid-run
 * returns the pre-sync (empty) state.
 */
export const isTerminalSyncStatus = (status?: string): boolean =>
  status !== undefined && status !== 'running' && status !== 'timeout';

export const syncAgent = async (
  slug: string,
  accountId: string,
  workspaceId: string,
  force: boolean = false,
  workspaceName?: string,
  options: SyncAgentOptions = {}
) => {
  const {
    onProgress,
    timeoutMs = SYNC_POLL_TIMEOUT_MS,
    pollIntervalMs = SYNC_POLL_INTERVAL_MS,
  } = options;
  const params = new URLSearchParams({ accountId, workspaceId });
  if (workspaceName) params.append('workspaceName', workspaceName);
  if (force) params.append('force', 'true');
  // Kick off background sync
  const response = await authFetch(`${API_URL}/api/agents/${encodeURIComponent(slug)}/sync?${params.toString()}`, {
    method: 'POST',
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(detail || `Sync failed (${response.status})`);
  }
  const kickoff = await response.json();
  if (kickoff.status === 'skipped') return kickoff;

  // Poll for completion (background task)
  const statusParams = new URLSearchParams({ accountId, workspaceId });
  if (workspaceName) statusParams.append('workspaceName', workspaceName);
  const statusUrl = `${API_URL}/api/agents/${encodeURIComponent(slug)}/sync-status?${statusParams.toString()}`;
  const startedAt = Date.now();
  const deadline = startedAt + timeoutMs;
  const noSyncDeadline = startedAt + SYNC_NO_SYNC_GRACE_MS;
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, pollIntervalMs));
    try {
      const statusResp = await authFetch(statusUrl);
      if (statusResp.ok) {
        const result = await statusResp.json();
        if (result?.status === 'running') {
          onProgress?.({
            stage: result.stage,
            processed: result.processed,
            total: result.total,
          });
          continue;
        }
        // Still waiting for the background task to register itself.
        if (result?.status === 'no_sync' && Date.now() < noSyncDeadline) {
          continue;
        }
        return result;
      }
    } catch { /* keep polling */ }
  }
  return { status: 'timeout', message: 'Sync is still running in the background' };
};
